'use client';

import React, { useState, useEffect, useRef, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button, Text, getAccessibleFocusStyles } from '@videoblocks/design-system';
import { ChevronDown } from '@videoblocks/react-icons';

import { formatDisplayText } from './utils';

const MultiselectDropdown = ({
    selected,
    options,
    onChange,
    header,
    onClear,
    onApply,
    disabled = false,
}: {
    selected: string[];
    options: string[];
    onChange: (value: string[]) => void;
    header: string;
    onClear?: () => void;
    onApply?: () => void;
    disabled?: boolean;
}): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [tempSelected, setTempSelected] = useState<string[]>(selected);
    const [openToRight, setOpenToRight] = useState(false);
    const [panelVisible, setPanelVisible] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTempSelected(selected);
    }, [selected]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
                setTempSelected(selected);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen, selected]);

    useEffect(() => {
        if (dropdownOpen && panelRef.current) {
            const rect = panelRef.current.getBoundingClientRect();
            if (rect.left < 0) {
                setOpenToRight(true);
            } else if (rect.right > window.innerWidth) {
                setOpenToRight(false);
            }
            setPanelVisible(true);
        } else {
            setPanelVisible(false);
            setOpenToRight(false);
        }
    }, [dropdownOpen]);

    const handleOptionToggle = (option: string) => {
        if (tempSelected.includes(option)) {
            setTempSelected(tempSelected.filter((item) => item !== option));
        } else {
            setTempSelected([...tempSelected, option]);
        }
    };

    const handleClear = () => {
        setTempSelected([]);
        if (onClear) {
            onClear();
        }
    };

    const handleApply = () => {
        onChange(tempSelected);
        setDropdownOpen(false);
        if (onApply) {
            onApply();
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Main Button */}
            <button
                onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
                className={twMerge(
                    'bg-gray-900 px-2 sm:px-4 h-14 rounded-md flex items-center gap-2 justify-between cursor-pointer hover:bg-black border border-gray-500',
                    disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                    getAccessibleFocusStyles(),
                )}
            >
                <Text fontSize="sm" fontWeight="regular">
                    {header}
                </Text>

                {!!selected?.length && (
                    <div className="bg-blue-700 rounded-full w-5 h-5 flex items-center justify-center font-semibold text-xs">
                        {selected.length}
                    </div>
                )}
                <ChevronDown
                    className={`fill-current w-3 h-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
                <div
                    ref={panelRef}
                    data-testid="multiselect-dropdown-panel"
                    className={twMerge(
                        'absolute top-full mt-1 bg-gray-900 rounded-lg shadow-xl z-20 border border-gray-700 min-w-[230px] overflow-hidden',
                        openToRight ? 'left-0' : 'right-0',
                        panelVisible ? 'visible' : 'invisible',
                    )}
                >
                    <div className="px-4 py-3 border-b border-gray-700">
                        <h3 className="text-white font-semibold text-lg">{header}</h3>
                    </div>

                    <div className="py-2 max-h-60 overflow-y-scroll [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-900 [&::-webkit-scrollbar-thumb]:bg-white">
                        {options.map((option) => (
                            <label
                                key={option}
                                className="w-full text-left px-4 py-1 flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={tempSelected.includes(option)}
                                    onChange={() => handleOptionToggle(option)}
                                    className={twMerge(
                                        'w-4 h-4 rounded border-gray-500 bg-gray-600 text-blue-500',
                                        getAccessibleFocusStyles(),
                                    )}
                                />
                                <Text fontSize="sm" fontWeight="regular" className="capitalize">
                                    {formatDisplayText(option)}
                                </Text>
                            </label>
                        ))}
                    </div>
                    <div className="flex items-center justify-between px-2 py-2 border-t border-gray-700 bg-gray-900">
                        <button
                            onClick={handleClear}
                            className={twMerge('text-sm px-6 cursor-pointer', getAccessibleFocusStyles())}
                        >
                            Clear all
                        </button>
                        <Button onClick={handleApply}>Apply</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiselectDropdown;
